/**
 * We use English comments only in this project.
 */
import { searchBounds, ringRoadBounds, serviceAreaCoordinates, defaultCenter } from './constants';

// Last search position tracking
let lastSearchedPosition = { lat: 0, lng: 0 };
const POSITION_THRESHOLD = 0.0002; // About 22 meters in lat/long difference

// Search status tracking
declare global {
  interface Window {
    _isNearbySearchInProgress?: boolean;
    _nearbySearchStartTime?: number;
    _nearbySearchRetryCount?: number;
    _nearbySearchTimeout?: number;
    _autocompleteCache?: WeakMap<HTMLInputElement, google.maps.places.Autocomplete | any>;
    _nearbySearchCache?: Map<string, {
      timestamp: number,
      results: { name: string, address: string }[]
    }>;
  }
}

// Add a declaration for our custom property
declare global {
  interface HTMLInputElement {
    __autocomplete_instance?: google.maps.places.Autocomplete;
  }
}

// Constants for search operations
const MAX_SEARCH_DURATION = 10000; // 10 seconds
const MAX_RETRY_COUNT = 3;

// Cache expiration time (increased to 15 minutes to reduce API calls)
const NEARBY_CACHE_EXPIRATION = 15 * 60 * 1000;

// Add more aggressive position threshold to reduce duplicate searches
const NEARBY_POSITION_THRESHOLD = 0.001; // About 111 meters - increased from previous smaller threshold

// Add request batching to prevent rapid-fire requests
let pendingNearbyRequests = new Map<string, Promise<{ name: string; address: string }[]>>();

// Add global request counter for monitoring
let nearbyRequestCount = 0;
const MAX_REQUESTS_PER_MINUTE = 5; // Reduced to 5 requests per minute to save costs
let requestTimestamps: number[] = [];

// Add feature flag to disable nearby searches when not needed
let nearbySearchEnabled = true;

// Function to enable/disable nearby searches
export const setNearbySearchEnabled = (enabled: boolean): void => {
  nearbySearchEnabled = enabled;
  console.log(`[Utils] Nearby search ${enabled ? 'enabled' : 'disabled'}`);
};

// Function to check if we're within rate limit
const isWithinRateLimit = (): boolean => {
  const now = Date.now();
  const oneMinuteAgo = now - 60000;
  
  // Remove timestamps older than 1 minute
  requestTimestamps = requestTimestamps.filter(timestamp => timestamp > oneMinuteAgo);
  
  return requestTimestamps.length < MAX_REQUESTS_PER_MINUTE;
};

// Function to record a new request
const recordRequest = (): void => {
  requestTimestamps.push(Date.now());
  nearbyRequestCount++;
  console.log(`[Utils] Places API request #${nearbyRequestCount} (${requestTimestamps.length} in last minute)`);
};

// Function to reset all nearby search state
const resetNearbySearchState = () => {
  window._isNearbySearchInProgress = false;
  window._nearbySearchStartTime = 0;
  window._nearbySearchRetryCount = 0;
  if (window._nearbySearchTimeout) {
    clearTimeout(window._nearbySearchTimeout);
    window._nearbySearchTimeout = undefined;
  }
};

// Debugging helper
export const logDebug = (_message: string, _data?: any) => {
  // No-op in production
};

// Check if location is within service area polygon using ray casting algorithm
// This is the SINGLE STANDARDIZED function for service area detection across the entire application.
// All other components should use this function to ensure consistent service area boundaries.
// Uses both ringRoadBounds for quick boundary check and serviceAreaCoordinates for precise polygon detection.
export const isLocationWithinServiceArea = (position: { lat: number; lng: number }): boolean => {
  // First quick boundary check (optimization)
  if (
    position.lat > ringRoadBounds.north ||
    position.lat < ringRoadBounds.south ||
    position.lng > ringRoadBounds.east ||
    position.lng < ringRoadBounds.west
  ) {
    return false;
  }
  
  // More precise polygon containment check using ray casting algorithm
  let isInside = false;
  const x = position.lng;
  const y = position.lat;
  
  for (let i = 0, j = serviceAreaCoordinates.length - 1; i < serviceAreaCoordinates.length; j = i++) {
    const xi = serviceAreaCoordinates[i].lng;
    const yi = serviceAreaCoordinates[i].lat;
    const xj = serviceAreaCoordinates[j].lng;
    const yj = serviceAreaCoordinates[j].lat;
    
    const intersect = ((yi > y) !== (yj > y)) && (x < (xj - xi) * (y - yi) / (yj - yi) + xi);
    if (intersect) isInside = !isInside;
  }
  
  return isInside;
};

// Helper to get preferred language based on browser settings
const getPreferredLanguage = (): string => {
  // Always use English as requested
  return 'en';
};

// Helper function to ensure Google Maps API is fully loaded
export const ensureGoogleMapsLoaded = (timeout = 10000): Promise<boolean> => {
  return new Promise((resolve) => {
    // Set Google Maps language to English globally
    if (window.google && window.google.maps) {
      // @ts-ignore - Force setting language globally
      window.google.maps.setLanguage && window.google.maps.setLanguage('en');
    }
    
    // If already loaded, resolve immediately
    if (window.google && window.google.maps && window.google.maps.places && 
       (window.google.maps.places.PlaceAutocompleteElement || window.google.maps.places.Autocomplete)) {
      console.log('Google Maps API already loaded');
      return resolve(true);
    }
    
    console.log('Waiting for Google Maps API to load...');
    
    // Set a timeout to prevent infinite waiting
    const timeoutId = setTimeout(() => {
      console.error('Google Maps API load timeout');
      resolve(false);
    }, timeout);
    
    // Check every 100ms if API is loaded
    const checkInterval = setInterval(() => {
      if (window.google && window.google.maps && window.google.maps.places && 
         (window.google.maps.places.PlaceAutocompleteElement || window.google.maps.places.Autocomplete)) {
        clearInterval(checkInterval);
        clearTimeout(timeoutId);
        console.log('Google Maps API loaded successfully');
        resolve(true);
      }
    }, 100);
  });
};

// Create Autocomplete instance - completely rewritten for reliability
export const createAutocomplete = (input: HTMLInputElement): google.maps.places.Autocomplete | null => {
  // Check if Google Maps API is loaded
  if (!window.google || !window.google.maps || !window.google.maps.places || !window.google.maps.places.Autocomplete) {
    console.error('Google Maps Places API is not available');
    return null;
  }
  
  try {
    console.log('Creating Autocomplete instance');
    
    // Clear any existing autocomplete attributes
    input.setAttribute('autocomplete', 'off');
    input.setAttribute('spellcheck', 'false');
    
    // Create autocomplete with explicit options to force English language
    const autocomplete = new window.google.maps.places.Autocomplete(input, {
      // Specify exactly what fields we need
      fields: ['name', 'geometry', 'formatted_address'],
      // Restrict to Thailand
      componentRestrictions: { country: 'th' },
      // Include both geocode and establishment types
      types: ['geocode', 'establishment']
    });
    
    // Force English language - most important setting
    autocomplete.setOptions({
      language: 'en'
    } as google.maps.places.AutocompleteOptions);
    
    // Style the dropdown to make it visible and properly positioned
    setTimeout(() => {
      const pacContainer = document.querySelector('.pac-container');
      if (pacContainer) {
        (pacContainer as HTMLElement).style.zIndex = '10000';
        (pacContainer as HTMLElement).style.position = 'fixed';
        console.log('Autocomplete dropdown styling fixed');
      }
    }, 500);
    
    console.log('Autocomplete created successfully');
    return autocomplete;
  } catch (error) {
    console.error('Error creating Autocomplete:', error);
    return null;
  }
};

// Extract location name from geocode result
export const extractLocationName = (result: google.maps.GeocoderResult): string => {
  if (!result) return 'Selected Location';
  
  if (result.address_components) {
    // Try to get establishment or point_of_interest first
    const poiComponent = result.address_components.find(component => 
      component.types.includes('establishment') || 
      component.types.includes('point_of_interest')
    );
    
    if (poiComponent) {
      return poiComponent.long_name;
    }
    
    // Fallback to premise or subpremise
    const premiseComponent = result.address_components.find(component => 
      component.types.includes('premise') || 
      component.types.includes('subpremise')
    );
    
    if (premiseComponent) {
      return premiseComponent.long_name;
    }
  }
  
  // Fallback to first part of formatted address
  return result.formatted_address?.split(',')[0] || 'Selected Location';
};

// Filter nearby places to meaningful locations
export const filterNearbyPlaces = (
  places: google.maps.places.PlaceResult[],
  currentAddress?: string
): { name: string, address: string }[] => {
  const filteredPlaces = places.filter(place => 
    place.name && 
    place.vicinity && 
    // Skip places that match the current location name if provided
    (currentAddress ? !place.vicinity.includes(currentAddress.split(',')[0]) : true) &&
    // Skip places that are just the city name
    !(place.name === 'Chiang Mai') &&
    // Ensure we have specific places with detailed addresses
    place.vicinity.split(',').length > 1
  );
  
  // Take up to 3 alternative locations
  return filteredPlaces.slice(0, 3).map(place => ({
    name: place.name || 'Nearby Location',
    address: place.vicinity || 'Nearby Address'
  }));
};

// Filter geocoder results to meaningful locations
export const filterGeocoderResults = (
  results: google.maps.GeocoderResult[]
): { name: string, address: string }[] => {
  const filteredResults = results.filter(result => 
    // Exclude results with very short formatted addresses (like just "Chiang Mai")
    result.formatted_address.split(',').length > 1 &&
    // Exclude results where the first component is just the city name
    result.formatted_address.split(',')[0] !== 'Chiang Mai'
  );
  
  return filteredResults.slice(0, 3).map(result => ({
    name: result.formatted_address.split(',')[0] || 'Alternative Location',
    address: result.formatted_address
  }));
};

// New function that creates an autocomplete instance using the new Place API
export const createModernAutocomplete = async (input: HTMLInputElement): Promise<google.maps.places.Autocomplete | null> => {
  // Check if Google Maps API is loaded
  if (!window.google || !window.google.maps) {
    console.error('Google Maps API is not available');
    return null;
  }

  // Use a static cache to avoid recreating instances for the same element
  if (!window._autocompleteCache) {
    window._autocompleteCache = new WeakMap();
  }

  // Check if we already have an instance for this input element
  if (window._autocompleteCache.has(input)) {
    console.log('Returning cached autocomplete instance');
    return window._autocompleteCache.get(input);
  }

  try {
    console.log('Creating modern Autocomplete instance');
    
    // Import the Places library if needed
    let placesLibrary;
    try {
      placesLibrary = await google.maps.importLibrary("places") as unknown as { Place: any, PlaceAutocompleteElement: any };
      console.log('Places library imported successfully');
    } catch (error) {
      console.error('Error importing Places library:', error);
      // Continue anyway as the global google.maps.places might still be available
    }
    
    // Force clear any existing instances
    let existingInstance = input.__autocomplete_instance;
    if (existingInstance) {
      try {
        google.maps.event.clearInstanceListeners(existingInstance);
        console.log('Cleared existing autocomplete instance');
      } catch (e) {
        console.error('Error clearing existing instance:', e);
      }
    }

    // Check if PlaceAutocompleteElement is available (new API)
    if (placesLibrary?.PlaceAutocompleteElement) {
      console.log('Using modern PlaceAutocompleteElement API');
      
      // Remove any existing autocomplete
      input.setAttribute('autocomplete', 'off');
      input.setAttribute('spellcheck', 'false');
      
      // Create a wrapper div to hold the PlaceAutocompleteElement
      const parentElement = input.parentElement;
      if (!parentElement) {
        console.error('Input element has no parent');
        return null;
      }
      
      // DON'T hide the original input - we want to keep it visible
      // Position the autocomplete container properly instead
      
      // Create the container for the PlaceAutocompleteElement
      const autocompleteContainer = document.createElement('div');
      autocompleteContainer.className = 'place-autocomplete-container';
      autocompleteContainer.style.width = '100%';
      parentElement.insertBefore(autocompleteContainer, input);
      
      // Create the PlaceAutocompleteElement
      const autocompleteElement = new placesLibrary.PlaceAutocompleteElement({
        inputElement: autocompleteContainer,
        componentRestrictions: { country: 'th' },
        types: ['geocode', 'establishment'],
        locationBias: {
          center: { lat: (searchBounds.north + searchBounds.south)/2, lng: (searchBounds.east + searchBounds.west)/2 },
          radius: 50000 // 50km radius around Chiang Mai
        }
      });
      
      // Create a proxy object that mimics the Autocomplete API for backward compatibility
      const autocompleteProxy = {
        addListener: (event: string, callback: Function) => {
          if (event === 'place_changed') {
            autocompleteElement.addEventListener('gmp-placeselect', (e: any) => {
              const place = e.detail.place;
              callback(place);
            });
          }
          return { remove: () => {} };
        },
        getPlace: () => {
          // This is a simplified proxy - the actual implementation would depend on usage
          return autocompleteElement.getPlace();
        },
        setOptions: (options: any) => {
          // This is a simplified proxy
          return;
        }
      };
      
      // Store reference to proxy for clean up
      input.__autocomplete_instance = autocompleteProxy as any;
      
      // Store in the cache
      window._autocompleteCache.set(input, autocompleteProxy as any);
      
      console.log('Modern PlaceAutocompleteElement created successfully');
      return autocompleteProxy as any;
    } else {
      console.log('PlaceAutocompleteElement not available, falling back to legacy Autocomplete');
      
      // Clear any existing autocomplete attributes
      input.setAttribute('autocomplete', 'off');
      input.setAttribute('spellcheck', 'false');
      
      // Create autocomplete with explicit options
      const autocomplete = new window.google.maps.places.Autocomplete(input, {
        // Specify exactly what fields we need
        fields: ['name', 'geometry', 'formatted_address', 'address_components'],
        // Restrict to Thailand
        componentRestrictions: { country: 'th' },
        // Include both geocode and establishment types
        types: ['geocode', 'establishment'],
        // Restrict to Chiang Mai area using bounds
        bounds: new google.maps.LatLngBounds(
          { lat: searchBounds.south, lng: searchBounds.west },
          { lat: searchBounds.north, lng: searchBounds.east }
        ),
        strictBounds: false // Allow results outside bounds but prioritize within
      });
      
      // Store reference to instance on the element itself for cleanup
      input.__autocomplete_instance = autocomplete;
      
      // Store in the cache
      window._autocompleteCache.set(input, autocomplete);
      
      // Force English language
      autocomplete.setOptions({
        language: 'en'
      } as google.maps.places.AutocompleteOptions);
      
      // Style the dropdown - use a more robust approach with multiple attempts
      const styleDropdown = () => {
        const pacContainer = document.querySelector('.pac-container');
        if (pacContainer) {
          (pacContainer as HTMLElement).style.zIndex = '10000';
          (pacContainer as HTMLElement).style.position = 'fixed';
          
          // Add additional styles to ensure visibility
          (pacContainer as HTMLElement).style.width = `${input.offsetWidth}px`;
          (pacContainer as HTMLElement).style.marginTop = '2px';
          (pacContainer as HTMLElement).style.boxShadow = '0 2px 10px rgba(0,0,0,0.2)';
          (pacContainer as HTMLElement).style.borderRadius = '8px';
          (pacContainer as HTMLElement).style.backgroundColor = '#ffffff';
          
          console.log('Autocomplete dropdown styled successfully');
          return true;
        }
        return false;
      };
      
      // Try styling immediately
      if (!styleDropdown()) {
        // Try again in 100ms
        setTimeout(() => {
          if (!styleDropdown()) {
            // Try again in 500ms
            setTimeout(styleDropdown, 500);
          }
        }, 100);
      }
      
      // Ensure dropdown is properly positioned when input receives focus
      input.addEventListener('focus', () => {
        console.log('Input focused, ensuring dropdown positioning');
        setTimeout(styleDropdown, 100);
      });
      
      console.log('Legacy Autocomplete created successfully');
      return autocomplete;
    }
  } catch (error) {
    console.error('Error creating modern Autocomplete:', error);
    return null;
  }
};

// Search for nearby places using the new Place API
export const findNearbyPlacesModern = async (
  map: google.maps.Map,
  position: { lat: number; lng: number },
  currentAddress?: string
): Promise<{ name: string; address: string }[]> => {
  try {
    console.log("[Utils] findNearbyPlacesModern called with position:", position);
    
    // Check if nearby search is disabled
    if (!nearbySearchEnabled) {
      console.log("[Utils] Nearby search is disabled, returning empty array");
      return [];
    }
    
    // Round position to 5 decimal places to handle tiny differences
    const roundedPosition = {
      lat: Math.round(position.lat * 100000) / 100000,
      lng: Math.round(position.lng * 100000) / 100000
    };
    
    // Create a cache key from the rounded position
    const cacheKey = `${roundedPosition.lat},${roundedPosition.lng}`;
    
    // Initialize cache if not exists
    if (!window._nearbySearchCache) {
      window._nearbySearchCache = new Map();
    }
    
    // Check if we have a valid cached result
    const cachedResult = window._nearbySearchCache.get(cacheKey);
    if (cachedResult && (Date.now() - cachedResult.timestamp < NEARBY_CACHE_EXPIRATION)) {
      console.log("[Utils] Returning cached nearby places result");
      return cachedResult.results;
    }
    
    // Check if there's already a pending request for this position
    if (pendingNearbyRequests.has(cacheKey)) {
      console.log("[Utils] Returning existing pending request for this position");
      return pendingNearbyRequests.get(cacheKey)!;
    }
    
    // Check rate limit before making new request
    if (!isWithinRateLimit()) {
      console.log("[Utils] Rate limit exceeded, returning cached result or empty array");
      return cachedResult?.results || [];
    }
    
    // Check if this is a duplicate call with the same position (using more aggressive threshold)
    const isSamePosition = 
      Math.abs(position.lat - lastSearchedPosition.lat) < NEARBY_POSITION_THRESHOLD && 
      Math.abs(position.lng - lastSearchedPosition.lng) < NEARBY_POSITION_THRESHOLD;
    
    // Check if a search has been running too long and reset if needed
    if (window._nearbySearchStartTime && (Date.now() - window._nearbySearchStartTime > MAX_SEARCH_DURATION)) {
      console.log("[Utils] Nearby search timeout exceeded, resetting state");
      resetNearbySearchState();
    }
    
    // Check if there is already an active search
    if (window._isNearbySearchInProgress) {
      console.log("[Utils] Nearby search already in progress, waiting...");
      
      // If already retried too many times or this is a duplicate call, return empty
      if (window._nearbySearchRetryCount && window._nearbySearchRetryCount >= MAX_RETRY_COUNT) {
        console.log("[Utils] Maximum retry count reached, returning empty result");
        resetNearbySearchState();
        return [];
      }
      
      // If this is a duplicate call for the same position, just return any cached results or empty
      if (isSamePosition) {
        console.log("[Utils] Duplicate call for same position, returning empty result");
        return cachedResult?.results || [];
      }
      
      // Wait a short time and try again with exponential backoff
      return new Promise((resolve) => {
        // Increment retry count
        window._nearbySearchRetryCount = (window._nearbySearchRetryCount || 0) + 1;
        
        // Calculate backoff time
        const backoffTime = Math.min(800 * Math.pow(1.5, window._nearbySearchRetryCount), 5000);
        
        window._nearbySearchTimeout = setTimeout(async () => {
          try {
            console.log(`[Utils] Retrying nearby search (attempt ${window._nearbySearchRetryCount})`);
            window._isNearbySearchInProgress = false;
            const result = await findNearbyPlacesModern(map, position, currentAddress);
            resolve(result);
          } catch (e) {
            console.error("[Utils] Error in retry attempt:", e);
            resetNearbySearchState();
            resolve([]);
          }
        }, backoffTime) as unknown as number;
      });
    }
    
    // Create a promise for this request and store it to prevent duplicates
    const searchPromise = (async (): Promise<{ name: string; address: string }[]> => {
      try {
        // Mark search started and initialize state
        window._isNearbySearchInProgress = true;
        window._nearbySearchStartTime = Date.now();
        window._nearbySearchRetryCount = 0;
        
        // Record this request for rate limiting
        recordRequest();
        
        // Update last search position
        lastSearchedPosition = { ...position };
        
        console.log("[Utils] Starting nearby search for position:", position);
      
        // Setup a global timeout to prevent infinite searching
        window._nearbySearchTimeout = setTimeout(() => {
          console.log("[Utils] Search timed out after", MAX_SEARCH_DURATION, "ms");
          resetNearbySearchState();
        }, MAX_SEARCH_DURATION) as unknown as number;
        
        // Load the Places library
        const { Place } = await google.maps.importLibrary("places") as { Place: any };
        
        // Create a LatLng object to handle the coordinates properly
        const center = new google.maps.LatLng(position.lat, position.lng);
        
        // Use the correct parameter structure for modern Places API with reduced parameters to save costs
        console.log("[Utils] Calling Places.searchNearby with reduced radius and result count");
        const response = await Place.searchNearby({
          fields: ['displayName', 'formattedAddress'], // Reduced fields to save costs
          // Use locationRestriction instead of location
          locationRestriction: {
            center: center,
            radius: 200, // Reduced from 500m to 200m to save costs
          },
          // Reduced parameters to save costs
          maxResultCount: 5, // Reduced from 10 to 5
          language: 'en'
        });
        
        console.log("[Utils] Nearby search response received:", response?.places?.length || 0, "places");
        
        // Extract relevant information
        const places = response.places || [];
        
        // End search status
        resetNearbySearchState();
        
        // Skip the wider radius search to reduce API calls - only use fallback if no results
        
        // Filter places
        const filteredPlaces = filterModernNearbyPlaces(places, currentAddress);
        console.log("[Utils] Filtered to", filteredPlaces.length, "nearby places:", filteredPlaces);
        
        // Store results in cache
        if (window._nearbySearchCache) {
          window._nearbySearchCache.set(cacheKey, {
            timestamp: Date.now(),
            results: filteredPlaces
          });
        }
        
        // Skip fallback search to reduce API calls - just return what we have
        if (filteredPlaces.length === 0) {
          console.log("[Utils] No filtered places found, skipping fallback to reduce API calls");
        }
        
        return filteredPlaces;
      } catch (error) {
        // Ensure search status is reset even in error cases
        resetNearbySearchState();
        
        console.error('[Utils] Error finding nearby places with modern API:', error);
        // Log the specific error details to help with debugging
        if (error instanceof Error) {
          console.error('[Utils] Error message:', error.message);
          console.error('[Utils] Error stack:', error.stack);
        }
        
        // Return empty array instead of fallback to reduce API calls
        return [];
      }
    })();
    
    // Store the promise to prevent duplicate requests
    pendingNearbyRequests.set(cacheKey, searchPromise);
    
    // Clean up the pending request when done
    searchPromise.finally(() => {
      pendingNearbyRequests.delete(cacheKey);
    });
    
    return searchPromise;
  } catch (error) {
    // Ensure search status is reset even in error cases
    resetNearbySearchState();
    
    console.error('[Utils] Error finding nearby places with modern API:', error);
    
    // Return empty array instead of fallback to reduce API calls
    return [];
  }
};

// Filter modern Places API results
export const filterModernNearbyPlaces = (
  places: any[],
  currentAddress?: string
): { name: string, address: string }[] => {
  return places.slice(0, 5)
    .map((place: any) => ({
      name: place.displayName || 'Nearby Location',
      address: place.formattedAddress || 'Nearby Address'
    }))
    .filter((place: any) => 
      place.name && 
      place.address && 
      // Skip places that match the current location name if provided
      (currentAddress ? !place.address.includes(currentAddress.split(',')[0]) : true) &&
      // Skip places that are just the city name
      place.name !== 'Chiang Mai' &&
      // Ensure we have specific places with detailed addresses
      place.address.split(',').length > 1
    )
    .slice(0, 3);
};

// Search by text query using the new Place API (replacing findPlaceFromQuery)
export const searchByTextModern = async (
  query: string
): Promise<{ position: { lat: number, lng: number }, address: string, locationName: string }> => {
  try {
    // Load the Places library
    const { Place } = await google.maps.importLibrary("places") as { Place: any };
    
    console.log("Searching with text query:", query);
    
    // Fix: Using the correct method name and structure for the Places API
    const request = {
      query: query,
      fields: ['name', 'geometry', 'formatted_address'],
      locationBias: new google.maps.LatLngBounds(
        { lat: searchBounds.south, lng: searchBounds.west },
        { lat: searchBounds.north, lng: searchBounds.east }
      )
    };
    
    // Create a PlacesService instance (need a HTML element)
    const placesService = new google.maps.places.PlacesService(
      document.createElement('div')
    );
    
    // Use the findPlaceFromQuery method with a Promise wrapper
    return new Promise((resolve, reject) => {
      placesService.textSearch(
        { query: query, bounds: request.locationBias },
        (results, status) => {
          if (status === google.maps.places.PlacesServiceStatus.OK && results && results.length > 0) {
            const place = results[0];
            console.log("Selected place details:", place);
            
            if (place.geometry && place.geometry.location) {
              resolve({
                position: {
                  lat: place.geometry.location.lat(),
                  lng: place.geometry.location.lng()
                },
                address: place.formatted_address || query,
                locationName: place.name || query
              });
            } else {
              reject(new Error('No geometry in the place result'));
            }
          } else {
            reject(new Error(`Place search failed with status: ${status}`));
          }
        }
      );
    });
  } catch (error) {
    console.error('Error searching by text with modern API:', error);
    // Log the specific error details to help with debugging
    if (error instanceof Error) {
      console.error('Error message:', error.message);
      console.error('Error stack:', error.stack);
    }
    throw error;
  }
};

// Fallback function for nearby search
const fallbackNearbySearch = async (
  map: google.maps.Map,
  position: { lat: number; lng: number },
  currentAddress?: string
): Promise<{ name: string; address: string }[]> => {
  console.log("[Utils] Using fallback nearby search method");
  try {
    // Create a PlacesService instance
    const service = new google.maps.places.PlacesService(map);
    
    return new Promise((resolve) => {
      service.nearbySearch({
        location: position,
        radius: 500,
        type: 'establishment'
      }, (results, status) => {
        if (status === google.maps.places.PlacesServiceStatus.OK && results) {
          const places = filterNearbyPlaces(results, currentAddress);
          console.log("[Utils] Fallback nearby search found:", places.length, "places");
          resolve(places);
        } else {
          console.log("[Utils] Fallback nearby search failed with status:", status);
          resolve([]);
        }
      });
    });
  } catch (fallbackError) {
    console.error("[Utils] Fallback nearby search also failed:", fallbackError);
    return [];
  }
}; 